import React from 'react'
import { Collapse } from 'antd'
import { QuestionCircleOutlined, LeftOutlined } from '@ant-design/icons'
import { useRouter } from 'next/router'
import type { GetStaticProps } from 'next'
import { breadcrumbLd, faqLd, SeoProps } from '@/utils/seo'

type FaqImage = { src: string; caption?: string }
type Faq = { q: string; a: string[]; images?: FaqImage[]; category: string }

// 分类顺序（按使用顺序）——新增分类只需往此数组加，并给对应问答标注 category
const CATEGORY_ORDER = ['总评寄语', '购买运输', '安装联网', '发牌设置', '应用场景', '复式比赛', '维护保养', '温馨提示']

// 灵缇发牌机百问百答 —— 持续补充,新增一条问答只需往此数组追加并标注 category
const faqs: Faq[] = [
  {
    category: '总评寄语',
    q: '灵缇发牌机只是代替人工发牌吗？',
    a: [
      '绝对不止这些！发牌机的使用，绝不是代替人工发牌这么简单。',
      '一是人工智能在娱乐游戏领域里的一次完美实践。',
      '二是彻底杜绝了现场摸牌、切牌、抓牌阶段的「鬼手」操作。',
      '三是促进了扑克比赛赛制的改革与创新。',
      '四是彻底解放了裁判。传统扑克裁判要熟练掌握米切尔移位赛、豪厄尔移位赛等繁琐复杂的赛制——要在最短时间内、用最少的牌组，完成「所有人与所有人见面、所有人与所有牌见面」，对裁判能力要求极高。发牌机的问世彻底颠覆了这一沿袭数十年的传统：它可以预制牌，轻松实现全场同时打同一副牌；裁判再也不用担心因某桌插错牌的方位、多牌少牌而导致成绩失真或比赛终止，也不用担心因时间、空间的错位，在复式赛时牌手之间相互传递、交流打过牌的信息。',
      '发牌机的问世与使用，是扑克竞技领域一座革命性的里程碑。',
      '——（老马视图）',
    ],
  },
  {
    category: '购买运输',
    q: '灵缇发牌机怎么购买？多少钱？',
    a: [
      '灵缇复式发牌机售价 9999 元，是同类机器中价格最优惠的。',
      '订购与咨询，请添加微信 lingtij（灵缇互娱），或点击本页下方的「联系我们」。',
    ],
  },
  {
    category: '购买运输',
    q: '灵缇发牌机交货时的包装形式？',
    a: [
      '通常以拉杆旅行箱的方式交付，便携又稳固。',
      '旅行箱外观尺寸：宽 42cm、厚 26cm、高 42cm（不包括伸缩拉杆与万向轮部分）。',
      '旅行箱及发牌机全重 8.5kg。',
    ],
    images: [{ src: '/fushiji/faq/packaging.jpg', caption: '拉杆旅行箱交付 · 便携稳固' }],
  },
  {
    category: '购买运输',
    q: '灵缇发牌机采取哪家快递运送？',
    a: [
      '鉴于发牌机的价值、重量与精密仪器设备的属性，高铁急送是我们的首选。',
      '以江苏苏州发往河北石家庄为例：第 1 天下午揽件后，第 2 天早上乘高铁起运，中午后抵达，客户下午即可到站自提。',
    ],
    images: [{ src: '/fushiji/faq/shipping.jpg', caption: '高铁急送 · 次日达 · 到站自提' }],
  },
  {
    category: '购买运输',
    q: '收到的灵缇发牌机都包括哪些组件？',
    a: ['包装箱内包括主机 1 台、电源适配器 1 个、4 方位牌仓盒 2 个。'],
    images: [
      { src: '/fushiji/faq/components-machine.jpg', caption: '主机 + 4 方位牌仓盒' },
      { src: '/fushiji/faq/components-adapter.jpg', caption: '电源适配器（24V / 3A）' },
      { src: '/fushiji/faq/components-inbox.jpg', caption: '整机置于旅行箱内' },
    ],
  },
  {
    category: '安装联网',
    q: '灵缇发牌机有使用说明书吗？',
    a: [
      '包装箱内没有纸质说明书。',
      '机器的侧面贴有「使用说明书」二维码，用微信「扫一扫」即可获取电子版使用说明书。',
      '说明书图文并茂，基本能够满足初次使用者的需求。',
    ],
    images: [{ src: '/fushiji/faq/manual-qr.jpg', caption: '机身侧面 · 扫码即可获取电子说明书' }],
  },
  {
    category: '安装联网',
    q: '第 1 次开机前还有哪些工作要做？',
    a: [
      '检查主机及配件齐全、外观完好后，在开机前准备以下物料：',
      '一是电源插座。厂家配备的电源适配器线长约 2m，考虑到工作便利，应在主机附近安排电源插排。',
      '二是两副扑克。用于测试发牌机。',
      '三是一部手机。用手机对发牌机进行全方位操控是最便捷的方式；灵缇发牌机默认只接受一台手机的操控，需要更换手机操控时，必须先断开原手机的连接。',
      '四是复式赛牌套。用于盛装发牌机分发完毕的牌组。',
    ],
  },
  {
    category: '安装联网',
    q: '开机前，与主机相关的哪些配件要全部就位？',
    a: [
      '一是将电源适配器的插头端接入电源插座，另一端插到主机开关旁的插孔内。',
      '二是将两副扑克牌牌面向下装入待分发牌仓。注意：需将扑克放到活动底板上方向下压，使扑克整齐地处于底板上方、滚轮下方的空间内，并向内靠近。',
      '三是将 4 方位牌仓盒插入发牌机的出牌口（两个金属立杆之间）。要求：仓盒底部到位，顶端（贴有灵缇互娱彩色 logo 的一面）水平，将仓盒凸起的两耳嵌入出牌口两侧壁上端的凹槽中。',
    ],
    images: [
      { src: '/fushiji/faq/setup-power.jpg', caption: '① 电源适配器接入主机开关旁插孔' },
      { src: '/fushiji/faq/setup-cards-before.jpg', caption: '② 放牌前 · 待分发牌仓' },
      { src: '/fushiji/faq/setup-cards-after.jpg', caption: '② 放牌后 · 扑克牌面向下装入' },
      { src: '/fushiji/faq/setup-tray-before.jpg', caption: '③ 放牌仓盒前' },
      { src: '/fushiji/faq/setup-tray-after.jpg', caption: '③ 放牌仓盒后 · logo 面朝上、水平贴合' },
    ],
  },
  {
    category: '安装联网',
    q: '开机后如何连接网络和进行初始设置？',
    a: [
      '发牌机通电开机后，即可通过灵缇互娱小程序连接发牌机。',
      '① 准备：开启所在环境的 Wi-Fi，并打开手机的星闪与蓝牙。',
      '② 进入灵缇互娱小程序，登录个人中心，点击首页右下角的「发牌机」板块。',
      '③ 点击设备后面的「未连接」按钮，等候显示「已连接」。',
      '④ 手动添加网络：选择网络并输入密码，使发牌机接入当前网络环境。',
      '至此，初始设置基本完成。',
    ],
    images: [
      { src: '/fushiji/faq/connect-home.jpg', caption: '① 小程序首页右下角进入「发牌机」' },
      { src: '/fushiji/faq/connect-device-off.jpg', caption: '② 点击设备后的「未连接」' },
      { src: '/fushiji/faq/connect-device-on.jpg', caption: '③ 等候显示「已连接」' },
      { src: '/fushiji/faq/connect-network-list.jpg', caption: '④ 手动添加网络 · 选择 Wi-Fi' },
      { src: '/fushiji/faq/connect-network-done.jpg', caption: '连接成功 · 已接入网络' },
    ],
  },
  {
    category: '发牌设置',
    q: '发牌前的发牌设置有哪些主要功能？',
    a: [
      '发牌设置是一个宏大的选项，包含的内容十分庞杂。',
      '首先，选择游戏类型。比如掼蛋、桥牌……后续还将陆续开发华牌（拱猪）、二打一（斗地主）、锄大地……',
      '其次，选择发牌模式。包括均衡牌力、随机发牌、特定牌型……',
      '再者，选择旧牌新用。从已有的牌谱库中选取某一编号的牌，再次发放使用。',
      '最后，选择牌型预览。将准备发放的各方向的牌一屏集中显示，便于甄别取舍。',
      '另外，还有一些发牌设置的延伸与拓展功能，将另文作专题介绍。',
    ],
  },
  {
    category: '发牌设置',
    q: '为什么发牌设置中，只有「牌力均衡」模式有下拉菜单选项？',
    a: [
      '「牌力均衡」模式中有两个下拉菜单选项：一个是这副牌所打的「级」，一个是首发的方位。这两项按照实际需求设定即可。',
      '同样是发牌设置，「随机发牌」等选项中都没有下拉菜单。',
      '这是因为，级牌在掼蛋中仅次于王牌，作用举足轻重。特别是红心级牌，变化莫测、威力无边。将级牌确定后，后台分发时才能做到真正意义上的均衡。',
      '而首发的方位，在游戏中也很重要。首发本身就相当于使用一次炸弹后得到的权利；而且首发的牌型及大小，直接影响后续行牌的走向，也会间接表达出首发者牌力的强弱与走上游的意向。',
      '所以，这两个下拉菜单选项不是画蛇添足，而是恰到好处的点睛之笔。',
      '——（老马视图）',
    ],
  },
  {
    category: '发牌设置',
    q: '发牌设置里有个「同花色」选项，有必要吗？',
    a: [
      '尽管笔者过去从没接触过灵缇发牌机，但拿到手一试，感觉和预期大体一致。只有一个小功能没在我的设想里，那就是——发「同花色」牌。有人可能觉得这是白痴设计，笔者却认为这是天才设计。',
      '笔者常年在各类比赛现场执裁，每次赛后桌面都会留下比赛用牌——升级、掼蛋，每桌两副。这些牌只用了几轮、打了半天或一天，基本都是八成新。只要主办方不要，我们裁判组都会收起来：一是节约，二是环保，三是送人，一举三得。',
      '问题是：赛场没有皮筋捆扎，甚至找不到原配的包装盒，也没有透明胶带。最快的办法，就是把各桌的牌摞在一起、装进塑料袋，最多用正、背区分。',
      '等这些牌送到公园里扑克爱好者的大爷大妈手中，第一件事就是发动大家把混在一起的多副牌按花色分开——张大爷负责红心、李大妈负责黑桃……现在好了，灵缇发牌机居然有「同花色」发牌选项，再也不用发动群众分牌了。',
      '——（老马视图）',
    ],
  },
  {
    category: '发牌设置',
    q: '「录入牌谱」功能有什么意义？',
    a: [
      '「录入牌谱」实现了从实体扑克到电子牌谱的转换与储存，充实和完善牌谱库，为长远建设与发展奠定基础。',
      '——（老马视图）',
    ],
    images: [
      { src: '/fushiji/faq/record-entry.jpg', caption: '底部「录入牌谱」入口' },
      { src: '/fushiji/faq/record-setup.jpg', caption: '选择牌谱类型 · 按 东→南→西→北 装牌' },
      { src: '/fushiji/faq/record-progress.jpg', caption: '录入中 · 等待机器完成' },
      { src: '/fushiji/faq/record-done.jpg', caption: '录入完成 · 牌张校验通过' },
      { src: '/fushiji/faq/record-detail.jpg', caption: '四家牌明细 · 各 27 张' },
      { src: '/fushiji/faq/record-library.jpg', caption: '我的牌库 · 选择已存牌谱' },
      { src: '/fushiji/faq/record-preview.jpg', caption: '发牌预览 · 四家牌一屏呈现' },
    ],
  },
  {
    category: '发牌设置',
    q: '什么样的牌型值得使用「录入牌谱」？',
    a: [
      '一副现实中的牌，一旦录入牌谱，就可以永久保存、随用随取。因此，值得录入的牌型大致有以下三类。',
      '一是经典牌例。大赛中的巅峰对决、一槌定音、以弱胜强……通过复盘和重打高手的牌局，体会、感悟、学习高手的基本功、判断力与记忆力。',
      '二是分歧牌例。同样一副牌，结果却南辕北辙——既有南北升三级，也有东西升三级。打这样的牌，可以分析行牌思路、解剖组牌策略、揣摩队友默契，分享掼蛋的无穷魅力。',
      '三是故事牌例。虽然这副牌波澜不惊、中规中矩，但当时围绕记忆、推理、配合，发生了有趣的小故事。录入牌谱，便可追忆当时的美好与快乐。',
      '当然，只要你认为值得，随时都可以录入牌谱。毕竟灵缇发牌机操作简单、用时短暂，随时随地都能轻而易举地完成。',
      '——（老马视图）',
    ],
  },
  {
    category: '发牌设置',
    q: '灵缇发牌机里的牌谱库，主要作用是什么？',
    a: [
      '灵缇发牌机可以存储大量牌谱。牌谱的来源有两个：一是本台发牌机曾经发出过的牌，二是扫描录入的有典型意义的牌。',
      '牌谱库的主要作用是用于教学与研究，训练并帮助牌手提高技战术水平。',
      '要注意：牌谱库中的牌型不适宜多次、频繁用于比赛场景。某个编号的牌谱即便用于不同的两场比赛，也可能同一个人先后参加了这两场——这会给参赛者带来不佳体验，甚至让人质疑主办方的专业性。',
      '——（老马视图）',
    ],
  },
  {
    category: '发牌设置',
    q: '「发牌记录」模块有哪些功能？',
    a: [
      '「发牌记录」模块位于页面的右下方。',
      '目前它的功能较为单一：忠实记录本台发牌机发过的所有牌型，每一副牌都记录着名称及时间。',
      '如果是已存在牌谱库中的牌，可以直接重复发出。',
      '——（老马视图）',
    ],
    images: [{ src: '/fushiji/faq/dealing-record-nav.jpg', caption: '底部右下角「发牌记录」入口' }],
  },
  {
    category: '应用场景',
    q: '灵缇发牌机只能用于正式的比赛吗？',
    a: [
      '灵缇发牌机的应用场景很广泛，覆盖娱乐、休闲、竞技等全部层级。',
      '无论是亲戚、朋友、同学、同事相聚时的快乐交流，还是两桌扑克爱好者的小型对抗，抑或是数十桌乃至上百桌的全国级及省级大型比赛，灵缇发牌机都有它的用武之地。',
      '灵缇发牌机快捷、高效、准确、科学，是扑克玩家及专业牌手的不二选择。',
      '——（老马视图）',
    ],
  },
  {
    category: '应用场景',
    q: '亲友聚会，餐前饭后打掼蛋，怎么使用灵缇发牌机？',
    a: [
      '「吃饭不掼蛋，等于没吃饭。」吃饭与掼蛋早已融为一体、密不可分。',
      '以一桌为例——这是灵缇发牌机最基础的应用款，原厂配置就能满足需求。',
      '物料准备：发牌机 1 台，原厂配备的 4 方位牌仓盒 2 个，扑克 4 副；其他还有专用桌布、报牌卡、记分表、笔等。',
      '① 启动发牌机，把发牌设置调到指定模式（比如随机发牌），进入准备发牌，不要点「预览牌型」。',
      '② 将 2 副牌装入进牌口，安装出口的牌仓盒，启动开始发牌，全程约 2 分钟。',
      '③ 取下出口的牌仓盒，按盒侧标注的方位，分别把牌发给 4 个方向的人。',
      '④ 4 人拿到牌后，按传统方式理牌、打牌。',
      '⑤ 打牌期间，把另 2 副牌装入进牌口、另一个牌仓盒装到出口，启动开始发牌。',
      '⑥ 打完不用洗牌，只需把 4 家的牌合成一墩。',
      '⑦ 重复 ③④⑤⑥，直至尽兴而归。',
      '——（老马视图）',
    ],
  },
  {
    category: '应用场景',
    q: '亲友聚会，用灵缇发牌机和传统手抓牌，差异在哪儿？',
    a: [
      '一是提高了效率。据统计，平时完成一次抓牌全程至少一分半钟，发牌机则要两分钟——机器岂不更慢？先别急着下结论：发牌机用两个牌仓盒、4 副牌交替进行，除第 1 副外，之后的发牌时间都淹没在打牌过程里，不多占一分钟。以通常 2 小时计，用发牌机比手抓牌能多打四五副牌以上。',
      '二是增加了话题。科技改变生活——谁能想到，在最不起眼的娱乐休闲里，也能看到科技的强力渗透。人工智能、互联网+ 这些高大上的词，竟也在为老百姓茶余饭后的娱乐服务。要是在场的好友里有一两位理工男，那更是给了他们展示专业的机会。',
      '三是轻松的「烦恼」。电脑麻将桌普及多年，川渝一带亲切地叫它「机麻」。用过机麻的人都有个感觉：省了洗牌、码牌的麻烦，也「剥夺」了休息、交流的机会。因为机麻的原理是一桌两副牌——一副在桌面上斗智斗勇，另一副在桌面下搅动、排列，两副无缝衔接、一气呵成。灵缇发牌机，情同此理。',
      '——（老马视图）',
    ],
  },
  {
    category: '复式比赛',
    q: '为什么扑克要打复式赛？',
    a: [
      '大家熟悉的娱乐项目，无非棋和牌两大类。棋类的特点是：开局时双方子力相同，或者地盘相同——起点本就公平。',
      '牌类却不一样：抓完牌后，你有王，我就没有王；你有大牌，我就只有小牌。同桌之间，牌力天生就不均衡。',
      '所以，复式赛应运而生。它的目的，就是通过在同一方向上比较同一副牌的结果，来解决同桌人员牌力不均衡的问题。',
      '拿王牌的与拿王牌的比，拿小牌的与拿小牌的比；好牌看谁赢得多，差牌看谁输得少。这样一来，比的就不再是运气，而是真本事。',
      '——（老马视图）',
    ],
  },
  {
    category: '复式比赛',
    q: '亲友聚会打掼蛋，除了用发牌机现场发牌，还有别的用法吗？',
    a: [
      '有。如果嫌发牌机有体积、有重量，完全可以换一种用法——打 1 桌 4 人的非正式复式赛。',
      '一场打几副不固定，可以是 N 副（N≥1）；下面以常见的 8 副为例。',
      '物料准备：发牌机 1 台，原厂配备的 4 方位牌仓盒 2 个，扑克 16 副，复式赛用牌套（盒）8 个；其他还有专用桌布、报牌卡、记分表、笔等。',
      '① 按 8 个牌套（盒）上的纸贴，确定每副牌所打的级数与首发方位，用发牌机做出 8 套预制牌，分别装入牌套（盒）备用。',
      '② 带着 8 套牌到现场（比发牌机轻多了）。',
      '③ 4 位朋友抽签确定队友，选边落座。',
      '④ 按牌套（盒）上的级数，从低到高依次打完 8 副牌，记录每副结果，大约 1 小时。',
      '⑤ 注意：打牌过程中严格把自己的牌放到自己的出牌区；一副结束后，把属于自己的 27 张牌，按所坐方位装入牌套（盒）对应的牌仓。',
      '⑥ 8 副打完，原来坐南北的人改坐东西，另一对相应调换。',
      '⑦ 把原来那 8 副牌，再按级数从低到高依次打完，记录每副结果，大约 1 小时。',
      '⑧ 前半场 8 副与后半场 8 副的结果对比，判定胜负。虽不是严格意义上的复式赛，却实现了「同桌同牌竞技」的初步要求。',
      '——（老马视图）',
    ],
  },
  {
    category: '复式比赛',
    q: '队式团体赛只有一套牌套（盒），怎么用灵缇发牌机完成比赛？',
    a: [
      '队式团体赛是最简单的复式赛，最小单元只需两桌——两队各出 4 人，分坐两桌较量。一桌开放允许观战、一桌封闭谢绝接近，民间又叫「开闭室比赛」。在这种比赛里，灵缇发牌机才真正走上赛场。',
      '一场打几副不固定，可以是 N 副（N≥1）；下面以常见的 8 副为例。',
      '物料准备：发牌机 1 台，原厂 4 方位牌仓盒 2 个，扑克 16 副，复式赛牌套（盒）8 个；另备桌布、报牌卡、记分表、笔等。',
      '① 依序选连续编号的 8 个牌套。有人习惯用 2～9（与平时打牌一致）；但从直观、管理、科学看，建议用 A～8。',
      '② 按 8 个牌套上的纸贴，定好每副牌的级数与首发方位，用发牌机做出 8 套预制牌，分别装入牌套备用。',
      '③ 两队分主、客，两桌分开室、闭室。主队坐开室东西 + 闭室南北，客队反之。',
      '④ 上半节：开室按顺序打 A、2 号牌，闭室按顺序打 3、4 号牌。每副打完必须复原、按方位装回牌套，并记录成绩；两副打完，开室与闭室的牌互换，继续打完。',
      '⑤ 交换座位：客队为尊，主队开室东西两位与闭室南北两位互换，实现主客全部对阵。',
      '⑥ 下半节：开室按顺序打 5、6 号牌，闭室按顺序打 7、8 号牌。同样每副复原装回、记录；两副打完后开闭室互换打完。',
      '⑦ 8 副全部打完，统计开室与闭室成绩，排定名次。',
      '——（老马视图）',
    ],
  },
  {
    category: '复式比赛',
    q: '队式团体赛用两套牌套（盒），怎么和灵缇发牌机配合完成比赛？',
    a: [
      '用两套牌套是队式团体赛的标准方式。加上灵缇发牌机，准确和效率都有了保证。',
      '一场打几副不固定，可以是 N 副（N≥1）；下面以常见的 8 副为例。',
      '物料准备：发牌机 1 台，原厂 4 方位牌仓盒 2 个，扑克 32 副，复式赛牌套（盒）16 个；另备桌布、报牌卡、记分表、笔等。',
      '① 用两套牌套，编号都是 A～8。',
      '② 按牌套纸贴定好每副的级数与首发方位、设好发牌设置，然后同一副牌发两遍，分别装入对应的两套牌套备用。这一步若手工操作：一慢、二易错、三过早暴露信息——发牌机把这几个短板补得干干净净。',
      '③ 预制好的 A～8 号牌，开室、闭室各放一套，顺序摆好。',
      '④ 上半节：开闭室同时开打，按顺序打 A～4 号牌，记录成绩。',
      '⑤ 交换座位：客队为尊，主队开室东西两位与闭室南北两位互换，实现主客全部对阵。',
      '⑥ 下半节：开闭室同时开打，按顺序打 5～8 号牌，记录成绩。',
      '⑦ 8 副打完，统计开闭室成绩，排定名次。',
      '用了两套牌套，就省去开闭室交换牌组的环节，也不必再让牌手把打过的牌复原装盒——裁判减负、牌手少犯错，这都得归功于发牌机介入了复式赛。',
      '——（老马视图）',
    ],
  },
  {
    category: '复式比赛',
    q: '大型队式团体赛，怎么发挥灵缇发牌机的优势？',
    a: [
      '两队两桌的队式团体赛最简单，过去靠裁判的经验，全手工也能完成。',
      '可要是 4 队 4 桌，过去就得裁判抽签把它们拆成两个单元（两组开闭室），用毫不相关的牌各打各的。虽然能产生两队胜者，但这两队打的是完全不同的牌，彼此没有可比性。',
      '灵缇发牌机的问世，把这套老做法彻底颠覆了。',
      '别说 4 队，以 10 队为例（理论上数十队乃至更多也行）都易如反掌。而这还不是关键——关键是能让全场打完全相同的牌！',
      '也就是说，10 队的比赛分成 5 个单元进行，每桌打同样的 N 副牌（N≥1，比如常见的 8 副）。因为是发牌机统一制作、完全相同的这 N 组牌，一轮下来，冠军队就出来了。',
      '当然，仅一轮可能碰上同分。那就按瑞士制的对阵原则——胜者对胜者，再打第 2、3、4 轮（10 个队打 4 轮，完全合规）。这几轮里，仍然靠发牌机实现全场同牌。',
      '这样产生的冠军，才无可争议。',
      '——（老马视图）',
    ],
  },
  {
    category: '复式比赛',
    q: '在队式团体赛中，怎样确定输赢？',
    a: [
      '队式团体赛是最简单的复式赛。所谓复式赛，就是把同样的牌交给不同的人去重复打两遍以上，然后进行比较——队式团体赛恰好满足了复式赛最基础的条件。',
      '复式赛的计分历来有很多分支，比如 IMP 分、VP 分等，有的侧重此、有的在意彼，各个计分体系既相互联系、又各有特色。仍以 8 副牌为例，常见的有三种。',
      '一是级差法（也叫 IMP 法）：简单地把主队与客队所打 8 副牌中的升级数相加，数值大者为胜。',
      '二是级差分法（也叫 VP 分法，拼音缩写 JCF）：把主队与客队所打 8 副牌的全部升级数相加，再根据双方的级差及所打牌的副数，套用一张换算表，得出双方最后的分值。通常采用 20 分制——双方共 20 分，此消彼长（见下表）。',
      '三是一副一比法：这里说的只是「比较的方法」。过去要等开室、闭室 8 副牌全部打完，裁判才能「一副一比」计分；而用灵缇发牌机做出两套牌后，开室与闭室完全同步进行，才名副其实地实现了「一副一比」。具体做法：每副牌 2 个场分，按双方同一副牌的对比结果给出——胜得 2 个场分，平各得 1 个场分。这种方法最接近比赛的「真相」，也让发牌机有了新的用武之地。',
      '——（老马视图）',
    ],
    images: [{ src: '/fushiji/faq/score-table-8.jpg', caption: '8 副 · 级差—级差分换算表（20 分制）' }],
  },
  {
    category: '复式比赛',
    q: '灵缇发牌机能用在米切尔复式赛中吗？',
    a: [
      '答案是肯定的——所有的复式赛都适合用灵缇发牌机备牌。',
      '米切尔复式赛是最常见的复式赛方式之一，也叫「双冠军赛」，通常适用于 3 桌、5 桌、7 桌……（双数桌称为「不完全米切尔」，会导致少打一轮牌）。',
      '用发牌机后，与传统米切尔赛的差异体现在三处。',
      '一是省去了牌手复原的环节。没有发牌机时，每副牌都要传到别桌继续打，所以牌手打完自己的牌后，必须清点 27 张、无误后按方位装入牌套（盒）——这个过程常出现多牌少牌、方向置换等问题，给比赛带来障碍与不便。',
      '二是省去了传牌的环节。过去打米切尔赛有句耳熟能详的口诀：「人往高处（大号）走，牌向低处（小号）流」——每打完一副，人要转、牌也要传。现在有了发牌机，直接取消了传牌：发牌机事先按各桌打牌顺序把牌做好，一步到位、全部就绪。',
      '三是提高了效率、减少了差错。取消上述两大环节，不仅省下大量时间，因牌手心理紧张或流程不熟导致的失误也几乎归零。',
      '——（老马视图）',
    ],
  },
  {
    category: '复式比赛',
    q: '豪厄尔复式赛能用灵缇发牌机吗？',
    a: [
      '答案是肯定的——所有的复式赛都适合用灵缇发牌机备牌。',
      '豪厄尔复式赛也是最常见的复式赛方式之一，又叫「单冠军赛」，通过极其复杂的人员流转与牌组交替，实现人员与牌组的全部相遇且不重复。',
      '用发牌机后，和米切尔赛一样：一是省去牌手复原的环节，二是省去传牌的环节，三是提高效率、减少差错。',
      '只是需要裁判提前用发牌机备牌，然后按各桌打牌的先后顺序，把牌套（盒）放到指定牌桌上；每打完一轮，引导牌手按新的对阵表就坐。',
      '——（老马视图）',
    ],
    images: [{ src: '/fushiji/faq/howell-5table.jpg', caption: '双人赛单冠军（豪厄尔移位）轮转表 · 5 桌（9 组牌）' }],
  },
  {
    category: '复式比赛',
    q: '用发牌机组织的大型复式赛，该怎么命名？',
    a: [
      '传统的复式赛只有三种方式：队式团体赛、米切尔移位赛、豪厄尔移位赛。而发牌机的问世，彻底颠覆了这一沿袭数十年的固定模式。',
      '由于发牌机可以实现全场同牌、全场同步，这给复式赛带来了革命性的变化——就连人们奉为圭臬的竞赛规则，对发牌机和新赛制也还是空白。于是，给这种比赛起一个合情、合理、合法的名字，成了当务之急。',
      '结合这些年发牌机的普及使用，以及全国各地的比赛实践，笔者认为命名为「瑞士制复式赛」最为贴切。',
      '一是比赛的内核是复式赛。因为有了发牌机，复式赛变得更简单；这种大型比赛无论双人赛还是队式赛，强调的都是复式打法，以体现科学与公平。',
      '二是比赛的编排是瑞士制。目前看，大大小小的双人赛与队式团体赛，都采用瑞士制编排，并遵循基本三原则：积分相同相近的对阵、同俱乐部的不对阵、已经对阵过的不再对阵。',
      '——（老马视图）',
    ],
  },
  {
    category: '复式比赛',
    q: '用发牌机组织大型瑞士制复式赛，要重点关注哪些环节？',
    a: [
      '用发牌机做好预制牌的比赛，与传统大型比赛完全不同。过去只要布置好场地、准备好牌具，人到齐（缺人也不影响）就能开赛，准备相对简单；而现在的大型瑞士制复式赛，又多了不少内容。',
      '一是发牌机就位。尽管牌已备好，小概率事件也可能发生——发牌机在现场，可随时复制任意牌型，及时弥补缺憾与失误。',
      '二是预制牌密封。把提前做好的牌放入合适的整理箱、贴上封条。箱子大小要适度：牌套（盒）有一定重量，加上两副牌，能放下 20 个左右牌套（盒）的箱子就够了；空间要紧凑，若有大缝隙最好用填充物塞实，防止牌张脱落惹麻烦。',
      '三是发牌后验牌。发完牌一定要复检：4 个方向的张数、东南西北的牌型都要逐一检验。从牌仓盒取出装入牌套（盒）的过程，最好双人操作、相互监督提醒。',
      '四是分发到各桌。牌套（盒）一定要水平搬放，保证牌张不掉；放到桌面时，要与桌面或推盘上的方向保持一致。',
      '——（老马视图）',
    ],
  },
  {
    category: '维护保养',
    q: '「设备管理」模块有哪些功能？',
    a: [
      '「设备管理」模块位于页面下方，一共有三个功能：断开蓝牙、牌张图片采集、固件升级。',
      '断开蓝牙：将现用手机的蓝牙关闭，解除与发牌机的绑定，方便其他管理人员用另一部手机重新扫码连接发牌机、进行后续操控。',
      '牌张图片采集：针对非标扑克、怪异画面等非常规扑克时使用（默认关闭，不写入牌张图片）。',
      '固件升级：公司不定期升级固件，需用户及时更新，以优化程序、更好地服务客户；升级包由发牌机联网下载，安装期间设备会自动重启，请勿断电。',
      '——（老马视图）',
    ],
    images: [
      { src: '/fushiji/faq/device-mgmt-entry.jpg', caption: '底部「设备管理」入口' },
      { src: '/fushiji/faq/device-mgmt.jpg', caption: '设备管理 · 断开蓝牙 / 牌张图片采集 / 固件升级' },
      { src: '/fushiji/faq/firmware-downloading.jpg', caption: '固件升级 · 正在下载升级包' },
      { src: '/fushiji/faq/firmware-done.jpg', caption: '固件升级 · 升级成功' },
    ],
  },
  {
    category: '维护保养',
    q: '灵缇发牌机的保管使用要注意什么？',
    a: [
      '无论个人、公司还是俱乐部，买了灵缇发牌机后都要妥善放置、精心维护，才能发挥最大效能。',
      '一是专人保管。大厂的精密仪器、大型设备都配有履历书记录情况；我们虽做不到这一点，但指定专人管理发牌机还是十分必要的。',
      '二是合理存放。厂家配的拉杆滚轮旅行箱，只是发牌机临时的「旅馆」；建议从网上买个合适的仪器箱，给它一个安全、舒适的「家」，把防尘、防潮落到实处。',
      '三是定期维护。包括擦拭、除尘——发牌机上有扫描识别组件，污垢杂物会直接影响识别准确率；也包括定期固件升级，只有及时更新，设备才能发挥最优效益。',
      '——（老马视图）',
    ],
  },
  {
    category: '温馨提示',
    q: '发牌机顶端的两个红色按钮，还有使用的功能吗？',
    a: [
      '有。长按红色按钮约 2 秒后，升降电机会复位到发牌位的最低点，用来校准发牌位置。',
      '一般情况下不需要按——正常发牌结束后会自动复位；只有在中途手动停止发牌时，才可能需要手动复位校准。',
      '目前两个按钮的功能相同，后期可能会拓展其他功能。',
    ],
  },
  {
    category: '温馨提示',
    q: '用灵缇发牌机为比赛做准备时，应注意哪些？',
    a: [
      '关注点很多，但最重要的只有一条——保密。传统手抓牌的比赛，除现场偷窥外不可能泄露牌型；而发牌机用预制牌备牌，就给了牌手猜测、想象的空间。',
      '一是发牌现场人员至简。裁判组精简到最少 2 人，相互督促、提醒、协作；主办方 1 人，主要行使监督职责。',
      '二是发牌现场全程录像。以现有技术并不复杂，既可用房间视频采集系统，也可临时架手机拍摄。要提醒的是：无论哪种方式，都要避免镜头直拍电脑屏幕与手机屏幕（与发牌机捆绑的手机）——发牌过程中，屏幕上可能呈现所发牌的全部牌型。',
      '三是备牌装箱贴封条。发好的预制牌装箱时要分类捆扎（把相同的牌集中放置），装完粘贴封条：一防无关人员无意翻动造成混乱，二防相关人员提前看到牌型、泄露秘密。',
      '——（老马视图）',
    ],
  },
]

const buildItem = (f: Faq, i: number) => {
  const imgs = f.images ?? []
  return {
    key: String(i),
    // 折叠的答案也渲染进 HTML(只是隐藏),搜索引擎/AI 能读到全部问答
    forceRender: true,
    label: (
      <span className="flex items-center gap-3">
        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-xs font-bold text-white">Q</span>
        <span className="text-base font-semibold text-gray-900 md:text-lg">{f.q}</span>
      </span>
    ),
    children: (
      <div className="pl-10">
        <div className="space-y-2">
          {f.a.map((line) => (
            <p key={line} className="text-[15px] leading-8 text-gray-700">
              {line}
            </p>
          ))}
        </div>
        {imgs.length > 0 && (
          <div className={`mt-4 grid gap-4 ${imgs.length > 1 ? 'sm:grid-cols-2' : ''}`}>
            {imgs.map((im) => (
              <figure key={im.src} className={imgs.length === 1 ? 'max-w-xl' : ''}>
                <img src={im.src} alt={im.caption || f.q} className="w-full rounded-2xl shadow-[0_10px_28px_rgba(0,0,0,0.12)]" />
                {im.caption && <figcaption className="mt-2 text-[13px] text-gray-400">{im.caption}</figcaption>}
              </figure>
            ))}
          </div>
        )}
      </div>
    ),
  }
}

const LingtiDealerFaqPage = () => {
  const router = useRouter()

  const groups = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: faqs.map((f, i) => ({ f, i })).filter((x) => x.f.category === cat),
  })).filter((g) => g.items.length > 0)

  return (
    <div className="min-h-screen text-gray-800">
      {/* 用 dangerouslySetInnerHTML:<style> 的文本子节点在服务端会被转义成 &#x27; 导致 CSS 失效 */}
      {/* eslint-disable-next-line react/no-danger */}
      <style dangerouslySetInnerHTML={{ __html: `
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          .faq-page * { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
          .fushiji-faq .ant-collapse-item {
            margin-bottom: 14px;
            background: rgba(255, 255, 255, 0.86);
            border: 1px solid rgba(255, 255, 255, 0.6) !important;
            border-radius: 16px !important;
            box-shadow: 0 4px 18px rgba(0, 0, 0, 0.06);
            overflow: hidden;
            transition: box-shadow 0.3s ease, transform 0.3s ease;
          }
          .fushiji-faq .ant-collapse-item:hover { box-shadow: 0 8px 26px rgba(59, 130, 246, 0.14); }
          .fushiji-faq .ant-collapse-item-active { box-shadow: 0 10px 30px rgba(59, 130, 246, 0.16); }
          .fushiji-faq .ant-collapse-header { padding: 18px 22px !important; align-items: center !important; }
          .fushiji-faq .ant-collapse-content-box { padding: 2px 22px 22px !important; }
        ` }}
      />

      <div className="fixed inset-0 -z-50">
        <img src="/fushiji/bg1.png" alt="背景" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-white/40" />
      </div>

      <section className="faq-page relative mx-auto max-w-4xl px-6 py-14 md:py-20">
        <button
          type="button"
          onClick={() => router.push('/fushiji')}
          className="mb-8 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white/70 px-4 py-1.5 text-sm text-gray-600 transition hover:border-blue-300 hover:text-blue-600"
        >
          <LeftOutlined className="text-xs" /> 返回发牌机
        </button>

        <div className="mb-10 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white/70 px-5 py-2 text-xs font-medium tracking-wider text-blue-700">
            <QuestionCircleOutlined /> 灵缇发牌机 · FAQ
          </div>
          <h1 className="mb-3 text-3xl font-bold leading-tight text-gray-900 md:text-4xl">灵缇发牌机百问百答</h1>
          <p className="text-base text-gray-500">从购买运输、安装联网、发牌设置，到应用场景、复式比赛、维护保养——分门别类，一站解答。</p>
        </div>

        {groups.map((g) => (
          <div key={g.cat} className="mb-9">
            <h2 className="mb-4 flex items-center gap-2.5 text-xl font-bold text-gray-900 md:text-2xl">
              <span className="inline-block h-6 w-1.5 rounded-full bg-gradient-to-b from-blue-600 to-indigo-600" />
              {g.cat}
              <span className="text-sm font-normal text-gray-400">· {g.items.length} 问</span>
            </h2>
            <Collapse items={g.items.map((x) => buildItem(x.f, x.i))} expandIconPosition="end" bordered={false} className="fushiji-faq bg-transparent" />
          </div>
        ))}

        <div className="mt-14 rounded-2xl border border-blue-100 bg-white/80 p-8 text-center shadow-sm">
          <h3 className="mb-2 text-xl font-bold text-gray-900">还有其他问题？</h3>
          <p className="mb-6 text-gray-500">7×24 小时客服随时为您解答</p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => router.push('/contact')}
              className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-3 font-semibold text-white shadow-lg transition hover:-translate-y-0.5"
            >
              联系我们
            </button>
            <button
              type="button"
              onClick={() => router.push('/fushiji')}
              className="rounded-xl border border-gray-200 bg-white px-8 py-3 font-semibold text-gray-700 transition hover:-translate-y-0.5 hover:border-blue-300"
            >
              了解发牌机
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const seo: SeoProps = {
    title: '灵缇复式发牌机百问百答 · 价格、安装联网、发牌设置、复式比赛 | 灵缇互娱',
    description: `灵缇复式发牌机常见问题 ${faqs.length} 问:多少钱、怎么购买、如何安装联网、发牌设置、支持哪些牌类、怎么办复式比赛、日常维护保养等,一站解答。`,
    keywords: '复式发牌机,发牌机价格,发牌机多少钱,掼蛋发牌机,桥牌发牌机,自动发牌机,灵缇发牌机,复式赛发牌',
    path: '/fushiji/faq',
    jsonLd: [
      faqLd(faqs.map((f) => ({ q: f.q, a: f.a.join(' ') }))),
      breadcrumbLd([
        ['首页', '/'],
        ['复式发牌机', '/fushiji'],
        ['百问百答', '/fushiji/faq'],
      ]),
    ],
  }
  return { props: { seo } }
}

export default LingtiDealerFaqPage
